import { commaListSchema, filterDateRangeSchema } from "@birdwatch/common";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { AiFeedbackReport } from "@/features/office/reports/ai-feedback";
import { SmartActionsGuard } from "@/features/office/smart-actions";
import { smartActionShadowSearchFragment } from "@/features/office/smart-actions/shadow";
import { AI_FEEDBACK_REPORT_ENABLED } from "@/features/shared/feature-gates";
import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

// Lenient URL schema (strict enums on URL params crash the page — see
// webapp-routing.md). Filter state follows the shared `filters-new` chip DSL:
// a `filters` object + `filtersOrder`; `sort` is the canonical `sort=-field`
// string the When header writes, and `aiFeedbackId` the open feedback.
const searchSchema = z.object({
  search: z.string().optional().catch(undefined),
  sort: z.string().optional().catch(undefined),
  aiFeedbackId: z.string().optional().catch(undefined),
  filters: z
    .object({
      // singleSelect chips store a SCALAR, not a comma list.
      rating: withOperators(z.string().optional()),
      reason: withOperators(z.string().optional()),
      reviewed: withOperators(z.string().optional()),
      subjectType: withOperators(commaListSchema),
      // The standard date-range chip value ({ start, end, label }).
      createdAt: filterDateRangeSchema.catch(undefined),
      ...notificationSearchFragment,
    })
    .optional(),
  ...filterSearchFragment,
  // A feedback's Smart Action opens over the report (`?smartActionId=`, SA-D33).
  ...smartActionShadowSearchFragment,
  ...maskSearchFragment,
});

export const routeId = "/office/reports/ai-feedback";
export const Route = createFileRoute("/office/reports/ai-feedback")({
  validateSearch: searchSchema,
  // While the report is hidden, an old link lands on the first report instead.
  beforeLoad: () => {
    if (!AI_FEEDBACK_REPORT_ENABLED) {
      throw redirect({ to: "/office/reports/order-overview" });
    }
  },
  component: AiFeedbackReportRoute,
});

/**
 * Behind the Smart Actions gate and the ADMIN / MANAGER role, deep link
 * included: Smart Actions is the only feature collecting AI feedback so far,
 * and the platform refuses every other role.
 */
function AiFeedbackReportRoute() {
  return (
    <SmartActionsGuard>
      <AiFeedbackReport />
    </SmartActionsGuard>
  );
}
